import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CTLNotification from "application/components/CTLNotification";
import { application } from "globals/endpoints";
import {
  Attributes,
  operationURLS,
  gridColumns,
  relatedColumns,
  detailGroups,
} from "./model";
import { fmtDateTime, fmtLocalInput, mmss } from "./formatters";
import { normalizeSearchNumber, computeValidationError, isReadyToSearch } from "./searchLogic";

function directionColor(dir) {
  if (dir === "IN") return "info";
  if (dir === "OUT") return "success";
  return "default";
}

function resultColor(cause) {
  if (!cause) return "default";
  if (cause === "NORMAL_CLEARING") return "success";
  if (cause === "ORIGINATOR_CANCEL") return "error";
  if (["USER_BUSY", "NO_ANSWER", "RECOVERY_ON_TIMER_EXPIRE"].includes(cause)) return "warning";
  return "default";
}

function hangupByLabel(disp) {
  if (disp === "recv-bye") return "Phone";
  if (disp === "send-bye") return "Server";
  return disp || "-";
}

// ---- cell renderer ---------------------------------------------------------
function renderCell(col, row) {
  const v = row[col.accessor];
  switch (col.type) {
    case "datetime":
      return fmtDateTime(v);
    case "mmss":
      return mmss(v);
    case "seconds":
      return v === null || v === undefined ? "" : `${v}s`;
    case "bool":
      return v ? "Yes" : "No";
    case "directionChip":
      // Static (uncolored) chip — color coding removed per Naveen so light + dark themes look the same.
      return v ? <Chip size="small" label={v} /> : "";
    case "resultChip":
      return v ? <Chip size="small" label={v} /> : "";
    case "hangupBy":
      return hangupByLabel(v);
    case "anchor":
      return row.anchor ? <Icon color="inherit">radio_button_checked</Icon> : <Icon>radio_button_unchecked</Icon>;
    default:
      if (col.sub && row[col.sub]) {
        return (
          <Box>
            <Typography variant="button" fontWeight="medium">{v}</Typography>
            <Typography variant="caption" display="block" color="text">{row[col.sub]}</Typography>
          </Box>
        );
      }
      return v === null || v === undefined ? "" : String(v);
  }
}

function RawCdrReport({ onLoad, pageName }) {
  // The codebase uses a central ThemeProvider that swaps themeDark/theme based
  // on the darkMode controller flag (see App.js:188,220 and CTLTable). Read the
  // same flag so we can pick MDTypography color tokens that match the rest of
  // the app — no per-component palette duplication.
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [searchNumber, setSearchNumber] = useState("");
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");

  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // client-side quick filters (operate on the already-loaded page)
  const [qType, setQType] = useState("ALL");
  const [qResults, setQResults] = useState([]);
  const [qHangupBy, setQHangupBy] = useState("ALL");
  const [qHasRecording, setQHasRecording] = useState(false);

  // row action menu — anchored to mouse position so it works for both
  // right-click on the row and click on the more_vert icon.
  const [menuPos, setMenuPos] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  // detail drawer
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // related drawer
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedRows, setRelatedRows] = useState([]);
  const [graceMinutes, setGraceMinutes] = useState(0);
  const [anchorRow, setAnchorRow] = useState(null);

  const [capWarning, setCapWarning] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "", pagename: "", status: "" });

  useEffect(() => {
    Attributes && onLoad && onLoad(Attributes);
  }, []);

  // Auto-populate intentionally removed in this pass — the heuristic was brittle
  // across browsers (some default the time to current time, not 00:00). A proper
  // fix needs separate date + time pickers (MUI x-date-pickers) and is queued for
  // after the theme work is closed (per Naveen, journal pending).

  // ---- validation ----
  const normalizedNumber = useMemo(() => normalizeSearchNumber(searchNumber), [searchNumber]);
  const validationError = useMemo(
    () => computeValidationError(normalizedNumber, fromTime, toTime),
    [normalizedNumber, fromTime, toTime]
  );
  const canSearch = isReadyToSearch(normalizedNumber, fromTime, toTime);

  function quickSelect(ms) {
    const now = new Date();
    const from = new Date(now.getTime() - ms);
    setFromTime(fmtLocalInput(from));
    setToTime(fmtLocalInput(now));
  }

  function buildRequest(extra) {
    return {
      searchNumber: normalizedNumber,
      startTime: fromTime,
      endTime: toTime,
      direction: null,
      hangupCause: null,
      context: null,
      sipDisposition: null,
      hasRecording: null,
      ...extra,
    };
  }

  function runSearch(nextPage = 0, nextSize = pageSize) {
    if (!canSearch) return;
    setLoading(true);
    setSearched(true);
    const countReq = buildRequest({});
    const listReq = buildRequest({ page: nextPage, pageSize: nextSize });
    Promise.all([
      application.post(operationURLS.count, countReq),
      application.post(operationURLS.list, listReq),
    ])
      .then(([countRes, listRes]) => {
        setTotal(Number(countRes?.data?.data ?? 0));
        setRows(listRes?.data?.data ?? []);
        setPage(nextPage);
        setPageSize(nextSize);
      })
      .catch((error) => {
        const msg = error?.response?.data?.message || "Search failed";
        setNotify({ isOpen: true, pagename: pageName, type: "error", status: msg });
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }

  const resultOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.hangupCause).filter(Boolean));
    return Array.from(set);
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (qType !== "ALL" && r.coralCallDirection !== qType) return false;
      if (qResults.length > 0 && !qResults.includes(r.hangupCause)) return false;
      if (qHangupBy !== "ALL" && r.sipHangupDisposition !== qHangupBy) return false;
      if (qHasRecording && !r.hasRecording) return false;
      return true;
    });
  }, [rows, qType, qResults, qHangupBy, qHasRecording]);

  function onChangePage(_e, newPage) {
    runSearch(newPage, pageSize);
  }
  function onChangeRowsPerPage(e) {
    runSearch(0, parseInt(e.target.value, 10));
  }

  function openRowMenu(e, row) {
    e.preventDefault();
    setMenuPos({ mouseX: e.clientX + 2, mouseY: e.clientY - 6 });
    setMenuRow(row);
  }
  function closeRowMenu() {
    setMenuPos(null);
    setMenuRow(null);
  }

  function openDetail(row) {
    closeRowMenu();
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    application
      .post(operationURLS.detail, { id: row.id })
      .then((res) => setDetailData(res?.data?.data || {}))
      .catch(() =>
        setNotify({ isOpen: true, pagename: pageName, type: "error", status: "Unable to load detail" })
      )
      .finally(() => setDetailLoading(false));
  }

  function openRelated(row) {
    closeRowMenu();
    setAnchorRow(row);
    // Reset slider to default 0 on each new anchor so the dialog never inherits
    // the previous row's slider position (code review feedback).
    setGraceMinutes(0);
    setRelatedOpen(true);
    fetchRelated(row, 0);
  }

  function fetchRelated(row, grace) {
    setRelatedLoading(true);
    setRelatedRows([]);
    application
      .post(operationURLS.related, { anchorUuid: row.uuid, graceMinutes: grace })
      .then((res) => setRelatedRows(res?.data?.data ?? []))
      .catch(() =>
        setNotify({ isOpen: true, pagename: pageName, type: "error", status: "Unable to load related CDRs" })
      )
      .finally(() => setRelatedLoading(false));
  }

  function downloadCsv() {
    setCapWarning(false);
    // Export must respect the active quick-filters so the CSV matches the filtered grid.
    const exportReq = buildRequest({
      direction: qType !== "ALL" ? qType : null,
      hangupCauses: qResults.length > 0 ? qResults : null,
      sipDisposition: qHangupBy !== "ALL" ? qHangupBy : null,
      hasRecording: qHasRecording ? true : null,
    });
    application
      .post(operationURLS.export, exportReq, { responseType: "arraybuffer" })
      .then((response) => {
        const capped = String(response?.headers?.["x-row-cap"] || "") === "true";
        const blob = new Blob([response.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "Raw-CDR.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        if (capped) setCapWarning(true);
        setNotify({ isOpen: true, pagename: pageName, type: "success", status: "CSV Download successful" });
      })
      .catch(() =>
        setNotify({ isOpen: true, pagename: pageName, type: "error", status: "Unable to download CSV" })
      );
  }

  return (
    <MDBox p={2}>
      {/* Search form — Paper surface so the card pops in both themes */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} mb={1.5}>
          <Typography variant="subtitle2" sx={{ width: { md: 220 }, flexShrink: 0, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.85 }}>
            Search Filters
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: { md: 1 }, justifyContent: "space-between" }} flexWrap="wrap">
            <MDButton size="small" variant="outlined" color="dark" onClick={() => quickSelect(3600 * 1000)}>1h</MDButton>
            <MDButton size="small" variant="outlined" color="dark" onClick={() => quickSelect(3 * 3600 * 1000)}>3h</MDButton>
            <MDButton size="small" variant="outlined" color="dark" onClick={() => quickSelect(6 * 3600 * 1000)}>6h</MDButton>
            <MDButton size="small" variant="outlined" color="dark" onClick={() => quickSelect(9 * 3600 * 1000)}>9h</MDButton>
            <MDButton size="small" variant="outlined" color="dark" onClick={() => quickSelect(12 * 3600 * 1000)}>12h</MDButton>
            <MDButton size="small" variant="outlined" color="dark" onClick={() => quickSelect(18 * 3600 * 1000)}>18h</MDButton>
            <MDButton size="small" variant="outlined" color="dark" onClick={() => quickSelect(24 * 3600 * 1000)}>24h</MDButton>
            <MDButton size="small" variant="outlined" color="dark" onClick={() => quickSelect(7 * 24 * 3600 * 1000)}>7d</MDButton>
          </Stack>
          <Box sx={{ width: 110, flexShrink: 0, display: { xs: "none", md: "block" } }} />
          <Box sx={{ width: 40, flexShrink: 0, display: { xs: "none", md: "block" } }} />
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <TextField
            label="Number or ID (caller or destination)"
            placeholder="e.g. +919876543210, 9876543210, 1001, Anonymous"
            value={searchNumber}
            onChange={(e) => setSearchNumber(e.target.value)}
            autoComplete="off"
            inputProps={{ autoComplete: "off", "data-lpignore": "true", "data-1p-ignore": "true", "data-form-type": "other" }}
            sx={{ width: { md: 220 }, flexShrink: 0 }}
          />
          <TextField
            label="From"
            type="datetime-local"
            value={fromTime}
            onChange={(e) => setFromTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { colorScheme: darkMode ? "dark" : "light" } }}
            sx={{ flex: { md: 1 } }}
          />
          <TextField
            label="To"
            type="datetime-local"
            value={toTime}
            onChange={(e) => setToTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { colorScheme: darkMode ? "dark" : "light" } }}
            sx={{ flex: { md: 1 } }}
          />
          <MDButton
            variant="outlined"
            color="dark"
            startIcon={<Icon>search</Icon>}
            disabled={!canSearch}
            onClick={() => runSearch(0, pageSize)}
            sx={{ width: 110, flexShrink: 0 }}
          >
            Search
          </MDButton>
          <Tooltip title="Download CSV">
            <span style={{ visibility: searched && !loading && total > 0 ? "visible" : "hidden" }}>
              <IconButton color="inherit" onClick={downloadCsv}>
                <Icon>download</Icon>
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {validationError && (
          <Typography variant="caption" color="error" mt={1} display="block">
            {validationError}
          </Typography>
        )}
      </Paper>

      {!searched && (
        <Typography variant="body2" color="text">
          Enter a phone number and time range, then click Search to view CDR records.
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {searched && !loading && total === 0 && (
        <Typography variant="body2" color="text">
          {`No CDR records found for number ${normalizedNumber} between ${fromTime} and ${toTime}.`}
        </Typography>
      )}

      {searched && !loading && total > 0 && (
        <>
          {capWarning && (
            <Box sx={{ p: 1, mb: 1, bgcolor: "warning.main", borderRadius: 1 }}>
              <Typography variant="caption" color="white">
                Export capped at 100,000 rows. Narrow your search for the full data set.
              </Typography>
            </Box>
          )}


          <TableContainer
            sx={{
              width: "100%",
              maxWidth: "100%",
              maxHeight: "calc(100vh - 320px)",
              overflow: "auto",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1800,
                "& .MuiTableCell-root": { whiteSpace: "nowrap" },
                "& .MuiTableCell-stickyHeader": {
                  backgroundColor: (theme) => theme.palette.background.card || theme.palette.white.main,
                },
              }}
            >
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {gridColumns.map((c) => (
                    <TableCell key={c.header}>{c.header}</TableCell>
                  ))}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onContextMenu={(e) => openRowMenu(e, row)}
                    sx={{ cursor: "context-menu" }}
                  >
                    {gridColumns.map((c) => (
                      <TableCell key={c.header}>{renderCell(c, row)}</TableCell>
                    ))}
                    <TableCell>
                      <IconButton size="small" onClick={(e) => openRowMenu(e, row)}>
                        <Icon>more_vert</Icon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={pageSize}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
          />
        </>
      )}

      <Menu
        open={Boolean(menuPos)}
        onClose={closeRowMenu}
        anchorReference="anchorPosition"
        anchorPosition={menuPos ? { top: menuPos.mouseY, left: menuPos.mouseX } : undefined}
      >
        <MenuItem onClick={() => openDetail(menuRow)}>View Details</MenuItem>
        <MenuItem onClick={() => openRelated(menuRow)}>Related CDRs</MenuItem>
      </Menu>

      {/* View Details dialog (post-test pivot: Drawer -> Dialog) */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">CDR Details</Typography>
            <IconButton onClick={() => setDetailOpen(false)}><Icon>close</Icon></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && <CircularProgress />}
          {!detailLoading && detailData && detailGroups.map((group) => {
            const present = group.fields.filter(
              (f) => detailData[f.key] !== null && detailData[f.key] !== undefined && detailData[f.key] !== ""
            );
            if (present.length === 0) return null;
            return (
              <Box key={group.title} mb={2}>
                <Typography variant="subtitle2" fontWeight="bold">{group.title}</Typography>
                {present.map((f) => (
                  <Stack key={f.key} direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="caption" color="text">{f.label}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: f.key.endsWith("uuid") ? "monospace" : "inherit", wordBreak: "break-all", textAlign: "right" }}>
                      {f.type === "datetime" ? fmtDateTime(detailData[f.key]) : String(detailData[f.key])}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>

      {/* Related CDRs dialog (post-test pivot: Drawer -> Dialog) */}
      <Dialog
        open={relatedOpen}
        onClose={() => setRelatedOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="column" spacing={0.25}>
              <Typography variant="h6">Related CDRs</Typography>
              {anchorRow && (
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {`Anchor: ${anchorRow.callerIdNumber || ""} → ${anchorRow.destinationNumber || ""} • ${fmtDateTime(
                    anchorRow.startEpoch
                  )}`}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <MDTypography variant="button" sx={{ whiteSpace: "nowrap" }}>
                {`Grace: ${graceMinutes} min`}
              </MDTypography>
              <Slider
                value={graceMinutes}
                onChange={(_e, v) => setGraceMinutes(v)}
                onChangeCommitted={(_e, v) => fetchRelated(anchorRow, v)}
                min={0}
                max={60}
                step={1}
                marks={[
                  { value: 0, label: "0" },
                  { value: 15, label: "15" },
                  { value: 30, label: "30" },
                  { value: 60, label: "60" },
                ]}
                valueLabelDisplay="auto"
                sx={{ width: 300 }}
              />
              <IconButton onClick={() => setRelatedOpen(false)}><Icon>close</Icon></IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {relatedLoading && <CircularProgress />}
          {!relatedLoading && relatedRows.length === 0 && (
            <Typography variant="body2" color="text">No related CDR legs found for this call.</Typography>
          )}
          {!relatedLoading && relatedRows.length > 0 && (
            <TableContainer sx={{ maxHeight: "calc(100vh - 280px)", overflow: "auto" }}>
              <Table
                stickyHeader
                size="small"
                sx={{
                  "& .MuiTableCell-stickyHeader": {
                    backgroundColor: (theme) => theme.palette.background.card || theme.palette.white.main,
                  },
                }}
              >
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    {relatedColumns.map((c) => (
                      <TableCell key={c.header}>{c.header}</TableCell>
                    ))}
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {relatedRows.map((row) => (
                    <TableRow key={row.id} hover selected={row.anchor}>
                      {relatedColumns.map((c) => (
                        <TableCell key={c.header}>{renderCell(c, row)}</TableCell>
                      ))}
                      <TableCell>
                        <Tooltip title="Open full CDR">
                          <IconButton size="small" onClick={() => { setRelatedOpen(false); openDetail(row); }}>
                            <Icon>open_in_new</Icon>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}

RawCdrReport.propTypes = {
  onLoad: PropTypes.func,
  pageName: PropTypes.string,
};

RawCdrReport.defaultProps = {
  onLoad: null,
  pageName: "Raw CDR Report",
};

export default RawCdrReport;
